import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { requireAuthenticatedContext } from '@/lib/auth';
import { logger } from '@/lib/logger';

export interface SplitItem {
  id: string;
  suffix: string;      // e.g., "L", "R", "1", "2"
  name?: string;       // Optional custom name (defaults to parent + suffix)
  category?: string;   // Can override parent category
  subcategory?: string; // Can override parent subcategory
  quantity: number;    // Usually 1
}

export interface SplitConfig {
  id?: string;
  reference_id: string;
  portal_product_id?: string;
  brand: string;
  model: string;
  split_config: SplitItem[];
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export class SplitConfigService {
  private orgId: string;

  constructor(orgId: string) {
    this.orgId = orgId;
  }

  private async getClient() {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Get split configuration for a product
   * Priority: reference_id > brand/model
   */
  async getSplitConfig(
    referenceId: string,
    brand?: string,
    model?: string
  ): Promise<SplitItem[] | null> {
    try {
      const supabase = await this.getClient();
      // First try by reference_id
      let { data, error } = await supabase
        .from('portal_org_splits')
        .select('split_config')
        .eq('clerk_org_id', this.orgId)
        .eq('reference_id', referenceId)
        .maybeSingle();

      // Fallback to brand/model if no reference_id match
      if (!data && brand && model) {
        const fallbackResult = await supabase
          .from('portal_org_splits')
          .select('split_config')
          .eq('clerk_org_id', this.orgId)
          .eq('brand', brand)
          .eq('model', model)
          .maybeSingle();
        
        data = fallbackResult.data;
        error = fallbackResult.error;
      }

      if (error && error.code !== 'PGRST116') {
        logger.error('Error fetching split config:', error);
        return null;
      }

      return data?.split_config as SplitItem[] || null;
    } catch (error) {
      logger.error('Error in getSplitConfig:', error);
      return null;
    }
  }

  /**
   * Save or update split configuration
   */
  async saveSplitConfig(
    referenceId: string,
    brand: string,
    model: string,
    splitItems: SplitItem[],
    portalProductId?: string
  ): Promise<boolean> {
    try {
      const supabase = await this.getClient();
      const context = await requireAuthenticatedContext();
      const userId = context?.userId;
      if (!userId) {
        logger.error('User not authenticated');
        return false;
      }
      const organizationId = context?.organizationId || this.orgId;

      const { error } = await supabase
        .from('portal_org_splits')
        .upsert({
          clerk_org_id: organizationId,
          reference_id: referenceId,
          portal_product_id: portalProductId,
          brand,
          model,
          split_config: splitItems,
          created_by: userId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'clerk_org_id,reference_id'
        });

      if (error) {
        logger.error('Error saving split config:', error);
        return false;
      }

      logger.debug('Split config saved successfully for reference_id:', referenceId);
      return true;
    } catch (error) {
      logger.error('Error in saveSplitConfig:', error);
      return false;
    }
  }

  /**
   * Delete split configuration
   */
  async deleteSplitConfig(referenceId: string): Promise<boolean> {
    try {
      const supabase = await this.getClient();
      const { error } = await supabase
        .from('portal_org_splits')
        .delete()
        .eq('clerk_org_id', this.orgId)
        .eq('reference_id', referenceId);

      if (error) {
        logger.error('Error deleting split config:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error in deleteSplitConfig:', error);
      return false;
    }
  }

  /**
   * Get all split configurations for the organization
   */
  async getAllSplitConfigs(): Promise<Map<string, SplitItem[]>> {
    try {
      const supabase = await this.getClient();
      const { data, error } = await supabase
        .from('portal_org_splits')
        .select('reference_id, brand, model, split_config')
        .eq('clerk_org_id', this.orgId);

      if (error) {
        logger.error('Error fetching all split configs:', error);
        return new Map();
      }

      const configs = new Map<string, SplitItem[]>();
      
      data?.forEach(item => {
        const splitConfig = item.split_config as SplitItem[];
        
        // Primary key: reference_id
        configs.set(item.reference_id, splitConfig);
        
        // Secondary key: brand:::model (for fallback)
        const brandModelKey = `${item.brand}:::${item.model}`;
        configs.set(brandModelKey, splitConfig);
      });

      return configs;
    } catch (error) {
      logger.error('Error in getAllSplitConfigs:', error);
      return new Map();
    }
  }

  /**
   * Check if a product has a split configuration
   */
  async hasSplitConfig(referenceId: string): Promise<boolean> {
    try {
      const supabase = await this.getClient();
      const { data, error } = await supabase
        .from('portal_org_splits')
        .select('id')
        .eq('clerk_org_id', this.orgId)
        .eq('reference_id', referenceId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        logger.error('Error checking split config:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      logger.error('Error in hasSplitConfig:', error);
      return false;
    }
  }

  /**
   * Apply split configuration to imported products
   * This expands products with split configs into individual split items
   */
  static applySplitsToProducts(
    products: any[],
    splitConfigs: Map<string, SplitItem[]>
  ): any[] {
    const processedProducts: any[] = [];

    for (const product of products) {
      // Check if this product has a split config
      const splitConfig = splitConfigs.get(product.reference_id) || 
                          splitConfigs.get(`${product.brand}:::${product.model}`);

      if (splitConfig && splitConfig.length > 0) {
        // Create split items
        splitConfig.forEach((split, index) => {
          const splitProduct = {
            ...product,
            // Create unique ID for split item
            id: `${product.id}_split_${split.suffix}`,
            // Use custom name or default to parent + suffix
            name: split.name || `${product.portal_name} ${split.suffix}`,
            portal_name: split.name || `${product.portal_name} ${split.suffix}`,
            // Allow category and subcategory override
            category: split.category || product.category,
            subcategory: split.subcategory || product.subcategory,
            // Split items usually have quantity 1
            quantity: split.quantity || 1,
            // Add split metadata
            is_split: true,
            split_suffix: split.suffix,
            split_parent_id: product.id,
            split_parent_reference: product.reference_id,
            split_index: index,
            split_config_id: split.id,
            // Preserve original data
            original_portal_data: product.portal_data
          };
          
          processedProducts.push(splitProduct);
        });
      } else {
        // No split config, add product as-is
        processedProducts.push(product);
      }
    }

    return processedProducts;
  }
}

export const createSplitConfigService = (orgId: string) => {
  return new SplitConfigService(orgId);
};
